import React from "react";
import { makeAPI } from "../../services/api";

function sanitizePhone(phone) {
    return String(phone || "").replace(/\D/g, "").slice(-10);
}

function getStoredUser() {
    try {
        const raw = localStorage.getItem("user");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch (error) {
        return null;
    }
}

export default function PhoneVerificationModal({
    isOpen,
    onClose,
    onVerified,
    user,
    title = "Verify your phone number",
    allowBypass = false,
    bypassLabel = "Skip for now",
    onBypass
}) {
    const [phone, setPhone] = React.useState("");
    const [otp, setOtp] = React.useState("");
    const [requestId, setRequestId] = React.useState("");
    const [busy, setBusy] = React.useState(false);
    const [errorMsg, setErrorMsg] = React.useState("");
    const [successMsg, setSuccessMsg] = React.useState("");
    const [otpSent, setOtpSent] = React.useState(false);
    const [cooldown, setCooldown] = React.useState(0);

    const currentUser = React.useMemo(() => getStoredUser() || user || null, [user]);
    const isPhoneVerified = currentUser?.phone_verified === true;

    React.useEffect(() => {
        if (!isOpen) return;
        const nextPhone = sanitizePhone(currentUser?.phone_no || "");
        setPhone(nextPhone);
        setOtp("");
        setRequestId("");
        setBusy(false);
        setErrorMsg("");
        setSuccessMsg("");
        setOtpSent(false);
        setCooldown(0);
    }, [isOpen, currentUser?.phone_no]);

    React.useEffect(() => {
        if (cooldown <= 0) return;
        const timer = setInterval(() => setCooldown((prev) => (prev > 0 ? prev - 1 : 0)), 1000);
        return () => clearInterval(timer);
    }, [cooldown]);

    if (!isOpen) return null;

    const withBusy = async (task) => {
        setBusy(true);
        setErrorMsg("");
        setSuccessMsg("");
        try {
            await task();
        } catch (error) {
            setErrorMsg(error?.message || "Something went wrong. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    const handleSendOtp = async () => {
        await withBusy(async () => {
            if (!currentUser?.user_id) {
                throw new Error("Please login to continue.");
            }

            const sanitizedPhone = sanitizePhone(phone);
            if (sanitizedPhone.length !== 10) {
                throw new Error("Phone number must be 10 digits.");
            }

            const response = await makeAPI("/api/send_phone_otp", "POST", false, {
                user_id: currentUser.user_id,
                phone_no: sanitizedPhone
            });

            if (response?.status !== "S") {
                throw new Error(response?.message || "Unable to send OTP.");
            }

            setPhone(sanitizedPhone);
            setOtpSent(true);
            setRequestId(response?.request_id || "");
            setCooldown(30);
            setSuccessMsg(response?.debug_otp ? `OTP sent. Debug OTP: ${response.debug_otp}` : "OTP sent successfully.");
        });
    };

    const handleVerifyOtp = async () => {
        await withBusy(async () => {
            if (!currentUser?.user_id) {
                throw new Error("Please login to continue.");
            }

            const sanitizedPhone = sanitizePhone(phone);
            if (sanitizedPhone.length !== 10) {
                throw new Error("Phone number must be 10 digits.");
            }

            if (!String(otp).trim()) {
                throw new Error("Please enter OTP.");
            }

            const response = await makeAPI("/api/verify_phone_otp", "POST", false, {
                user_id: currentUser.user_id,
                phone_no: sanitizedPhone,
                otp: String(otp).trim(),
                request_id: requestId
            });

            if (response?.status !== "S") {
                throw new Error(response?.message || "Unable to verify OTP.");
            }

            const stored = getStoredUser() || currentUser;
            const updatedUser = {
                ...stored,
                phone_no: sanitizedPhone,
                phone_verified: true
            };
            localStorage.setItem("user", JSON.stringify(updatedUser));
            window.dispatchEvent(new CustomEvent("auth:updated"));

            setSuccessMsg("Phone number verified successfully.");
            if (typeof onVerified === "function") {
                onVerified(updatedUser);
            }
        });
    };

    return (
        <>
            <style>
                {`
                    .phone-verify-modal {
                        position: fixed;
                        inset: 0;
                        z-index: 1200;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        background: rgba(0, 0, 0, 0.7);
                        padding: 16px;
                    }
                    .phone-verify-card {
                        position: relative;
                        width: 100%;
                        max-width: 480px;
                        background: #161616;
                        border: 1px solid #303030;
                        border-radius: 10px;
                        padding: 20px;
                        color: #fff;
                    }
                    .phone-verify-actions {
                        display: flex;
                        gap: 10px;
                        justify-content: flex-end;
                        margin-top: 16px;
                    }
                `}
            </style>
            <div
                className="phone-verify-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="phone-verify-title"
                onClick={() => {
                    if (busy) return;
                    onClose?.();
                }}
            >
                <div className="phone-verify-card" onClick={(event) => event.stopPropagation()}>
                <button
                    type="button"
                    className="uk-modal-close-default"
                    aria-label="Close"
                    onClick={() => {
                        if (busy) return;
                        onClose?.();
                    }}
                >
                    x
                </button>
                <h5 id="phone-verify-title" style={{ marginBottom: "12px" }}>{title}</h5>

                {isPhoneVerified ? (
                    <div>
                        <p style={{ marginBottom: "8px" }}>
                            Your phone number is already verified.
                        </p>
                        <input
                            type="text"
                            className="uk-input"
                            value={currentUser?.phone_no || ""}
                            disabled
                            style={{ marginBottom: "10px" }}
                        />
                        <small style={{ color: "#a7a7a7" }}>Verified number cannot be edited.</small>
                    </div>
                ) : (
                    <div>
                        <label style={{ display: "block", marginBottom: "6px" }}>Phone Number</label>
                        <input
                            type="text"
                            className="uk-input"
                            inputMode="numeric"
                            maxLength={10}
                            value={phone}
                            placeholder="Enter 10-digit phone number"
                            onChange={(event) => setPhone(sanitizePhone(event.target.value))}
                            style={{ marginBottom: "10px" }}
                            disabled={busy || otpSent}
                        />

                        {otpSent ? (
                            <>
                                <label style={{ display: "block", marginBottom: "6px" }}>OTP</label>
                                <input
                                    type="text"
                                    className="uk-input"
                                    inputMode="numeric"
                                    maxLength={6}
                                    value={otp}
                                    placeholder="Enter OTP"
                                    onChange={(event) => setOtp(String(event.target.value || "").replace(/\D/g, ""))}
                                />
                            </>
                        ) : null}
                    </div>
                )}

                {errorMsg ? (
                    <div className="uk-alert-danger uk-margin-small" style={{ marginTop: "12px" }}>
                        {errorMsg}
                    </div>
                ) : null}
                {successMsg ? (
                    <div className="uk-alert-success uk-margin-small" style={{ marginTop: "12px" }}>
                        {successMsg}
                    </div>
                ) : null}

                <div className="phone-verify-actions">
                    <button
                        type="button"
                        className="uk-button uk-button-default"
                        onClick={() => {
                            if (busy) return;
                            onClose?.();
                        }}
                        disabled={busy}
                    >
                        Close
                    </button>
                    {!isPhoneVerified && allowBypass ? (
                        <button
                            type="button"
                            className="uk-button uk-button-default"
                            onClick={() => {
                                if (busy) return;
                                if (typeof onBypass === "function") {
                                    onBypass();
                                }
                                onClose?.();
                            }}
                            disabled={busy}
                        >
                            {bypassLabel}
                        </button>
                    ) : null}
                    {isPhoneVerified ? null : (
                        otpSent ? (
                            <>
                                <button
                                    type="button"
                                    className="uk-button uk-button-default"
                                    onClick={handleSendOtp}
                                    disabled={busy || cooldown > 0}
                                >
                                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                                </button>
                                <button
                                    type="button"
                                    className="uk-button uk-button-danger"
                                    onClick={handleVerifyOtp}
                                    disabled={busy}
                                    style={{ color: "#fff" }}
                                >
                                    {busy ? "Please wait..." : "Verify OTP"}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="uk-button uk-button-danger"
                                onClick={handleSendOtp}
                                disabled={busy}
                                style={{ color: "#fff" }}
                            >
                                {busy ? "Please wait..." : "Send OTP"}
                            </button>
                        )
                    )}
                </div>
                </div>
            </div>
        </>
    );
}
